import React, { useState, useRef } from 'react';
import { View, Platform } from 'react-native';
import { useSelector } from 'react-redux';
import IconInput from 'src/components/IconInput/IconInput';
import CustomDatePicker from 'src/components/CustomDatePicker/CustomDatePicker';
import InputField from 'src/components/input/input';
import WebDatePickerInput from 'src/components/WebDatePickerInput/WebDatePickerInput';
import TourWrapper from 'src/components/TourWrapper/TourWrapper';
import { RootState } from 'src/store/store';
import styles from './CompanyDetailsScreen.styles';
import date from 'src/assets/date.png';
import { Company } from '@typeRoots/common/company';
import { formatToUSDate } from 'src/helpers/dateHelper';
import InputHint from 'src/components/input/InputError';
import {
  handleInputChange,
  getFieldProps,
  inputFieldsConfig,
  isDotNumberValid,
} from 'src/features/company/helpers/companyHelpers';

interface InputFieldsProps {
  formData: Company;
  onChange: (field: keyof Company, value: string | number | boolean) => void;
  hideDotField?: boolean;
}

const InputFields: React.FC<InputFieldsProps> = ({ formData, onChange, hideDotField = false }) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const getTourId = (field: keyof Company): string | undefined => {
    const tourIdMap: Partial<Record<keyof Company, string>> = {
      companyName: 'company-name-input',
      dba: 'dba-input',
      dotNumber: 'dot-number-input',
      mcNumber: 'mc-number-input',
    };
    return tourIdMap[field];
  };

  // Track whether the DOT Number field was initially filled
  const isDotNumberInitiallyFilled = useRef(
    !!formData.dotNumber && isDotNumberValid(formData.dotNumber),
  );

  const accountType = useSelector((state: RootState) => state.UI.accountType);
  const isProducerWeb = accountType === 'producer' && Platform.OS === 'web';

  // Filter out DOT field if user came from garaging address flow
  const fieldsToRender = hideDotField
    ? inputFieldsConfig.filter(({ field }) => field !== 'dotNumber')
    : inputFieldsConfig;

  return (
    <>
      {fieldsToRender.map(({ label, field, testID }) => {
        const { variant, maxLength } = getFieldProps(field);
        const tourId = getTourId(field);

        return (
          <TourWrapper key={field} tourId={tourId}>
            <View style={styles.fieldContainer}>
              <InputField
                testID={testID}
                label={label}
                value={formData[field]?.toString() || ''}
                onChange={(val) => handleInputChange(field, val, onChange)}
                variant={variant}
                maxLength={maxLength}
                required={false}
                editable={field !== 'dotNumber' || !isDotNumberInitiallyFilled.current} // Disable editing only if DOT Number was initially filled
              />
              {/* Show hint when the field is DOT Number and the DOT Number is valid and initially not filled */}
              {field === 'dotNumber' && isDotNumberInitiallyFilled.current && (
                <InputHint hintText="The DOT number can only be updated in your Trucker Path profile" />
              )}
            </View>
          </TourWrapper>
        );
      })}

      <TourWrapper tourId="business-start-date">
        <View style={styles.fieldContainer}>
          {isProducerWeb ? (
            <WebDatePickerInput
              testID="landing-business-start-date-edit"
              label="What day did the business start?"
              valueISO={formData?.dateBusinessStarted ?? null}
              onChangeISO={(iso) => onChange('dateBusinessStarted', iso ?? '')}
            />
          ) : (
            <>
              <IconInput
                testID="landing-business-start-date-edit"
                label="What day did the business start?"
                value={
                  formData?.dateBusinessStarted ? formatToUSDate(formData.dateBusinessStarted) : ''
                }
                onPress={() => setIsDatePickerOpen(true)}
                onIconPress={() => setIsDatePickerOpen(true)}
                placeholder="Select a date"
                iconSource={date}
                iconWidth={20}
                iconHeight={20}
                required={false}
              />
              <CustomDatePicker
                value={new Date(formData.dateBusinessStarted || Date.now())}
                isOpen={isDatePickerOpen}
                onSelect={(d) => {
                  // Always store ISO (YYYY-MM-DD)
                  onChange('dateBusinessStarted', d.toISOString().split('T')[0]);
                  setIsDatePickerOpen(false);
                }}
                onCancel={() => setIsDatePickerOpen(false)}
              />
            </>
          )}
        </View>
      </TourWrapper>

      {isProducerWeb && (
        <View style={styles.fieldContainer}>
          <WebDatePickerInput
            testID="company-authority-start-date"
            label="Authority Start Date"
            valueISO={formData?.authorityStartDate ?? null}
            onChangeISO={(iso) => onChange('authorityStartDate', iso ?? '')}
          />
        </View>
      )}

      {isProducerWeb && (
        <View style={styles.fieldContainer}>
          <InputField
            testID="company-coverage-lapse-days"
            label="Coverage Lapse Days"
            value={formData?.coverageLapseDays?.toString() ?? ''}
            placeholder="Enter number of days (0 = no lapse)"
            onChange={(val) => {
              const parsed = parseInt(val, 10);
              onChange('coverageLapseDays', val === '' ? 0 : isNaN(parsed) ? 0 : parsed);
            }}
            variant="caption"
          />
        </View>
      )}
    </>
  );
};

export default InputFields;
